import type { IUiDialogHookCustomContent } from '@experiences/interfaces';
import {
    UiDividerWithText,
    UiText,
} from '@experiences/ui-common';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, {
    useCallback,
    useState,
} from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';

import { notificationType } from '../../../common/constants/Constant';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import { inviteUsers } from '../../../services/identity/UserService';

const RESEND_INVITE_TIMEOUT_INTERVAL_SECONDS = 60;

const useStyles = makeStyles(theme =>
    createStyles({
        inviteUserUrlBox: {
            width: '100%',
            marginTop: '8px',
        },
        dialogBody: { minWidth: '500px' },
        copyInvitationLink: {
            marginTop: '16px',
            marginBottom: '33px',
            color: theme.palette.semantic.colorPrimaryFocused,
            display: 'flex',
            alignItems: 'center',
            fontWeight: 600,
            paddingLeft: '0px',
        },
        copyInvitationLinkIcon: { marginRight: '10px' },
        divider: { marginTop: '16px' },
        sendEmailAgain: {
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        sendEmailAgainButton: {
            color: theme.palette.semantic.colorPrimaryFocused,
            fontWeight: 600,
        },
        sendEmailAgainIcon: { marginRight: '10px' },
    }),
);

const ResendInvitationDialogBody: React.FC<IUiDialogHookCustomContent> = ({
    closeDialog,
    user,
    orgLanguage,
    disabledEmails,
    setDisabledEmails,
    loginProvider,
    inviteUrl,
}) => {
    const classes = useStyles();
    const createNotification = useUiSnackBar();
    const { formatMessage: translate } = useIntl();
    const [ copiedText, setCopiedText ] = useState('');

    const findDisabledEmail = useCallback(() => !!disabledEmails.find((email: string) => email === user?.email), [ disabledEmails, user ]);

    const disableEmailSend = useCallback(() => {
        setDisabledEmails((emails: string[]) => [ ...emails, user?.email ]);
        setTimeout(() => {
            setDisabledEmails((emails: string[]) => emails.filter((disabled: string) => disabled !== user?.email));
        }, RESEND_INVITE_TIMEOUT_INTERVAL_SECONDS * 1000);
    }, [ user, setDisabledEmails ]);

    const sendEmailAgain = useCallback(async () => {
        try {
            await inviteUsers(
                [
                    {
                        name: user.name,
                        email: user.email,
                        groupIDs: user.groupIDs,
                    },
                ],
                loginProvider,
                orgLanguage,
            );
            disableEmailSend();
            createNotification(translate({ id: 'CLIENT_RESEND_SUCCESS' }), notificationType.SUCCESS);
            closeDialog(true);
        } catch (error) {
            setDisabledEmails((emails: string[]) => emails.filter((disabled: string) => disabled !== user?.email));
            createNotification(translate({ id: 'CLIENT_RESEND_FAILED' }), notificationType.ERROR);
        }
    }, [ translate, closeDialog, createNotification, orgLanguage, user, setDisabledEmails, loginProvider, disableEmailSend ]);

    return (
        <div className={classes.dialogBody}>
            <UiText data-cy="copy-to-clipboard-reminder">
                {translate({ id: 'CLIENT_COPY_TO_CLIPBOARD_REMINDER' })}
            </UiText>
            <TextField
                className={classes.inviteUserUrlBox}
                variant="outlined"
                value={inviteUrl}
                data-cy="invite-user-url"
                multiline
                disabled
                inputProps={{ 'aria-label': translate({ id: 'CLIENT_INVITATION_LINK_FIELD_ARIA' }) }}
            />

            <CopyToClipboard
                text={inviteUrl}
                onCopy={() => setCopiedText('copied')}>
                <Tooltip
                    title={
                        copiedText === 'copied'
                            ? translate({ id: 'CLIENT_INVITATION_LINK_COPIED' })
                            : translate({ id: 'CLIENT_COPY_TO_CLIPBOARD' })
                    }
                    placement="top"
                >
                    <Button
                        className={classes.copyInvitationLink}
                        data-cy="copy-invitation-link"
                        id="copyInvitationLink">
                        <FileCopyOutlinedIcon
                            color="primary"
                            className={classes.copyInvitationLinkIcon}
                            fontSize="small"
                            aria-labelledby="copyInvitationLink"
                            data-cy="copy-invitation-link-icon"
                        />
                        {translate({ id: 'CLIENT_COPY_INVITATION_LINK' })}
                    </Button>
                </Tooltip>
            </CopyToClipboard>
            <UiDividerWithText text={translate({ id: 'CLIENT_OR' })} />

            <Tooltip
                arrow
                title={findDisabledEmail() ? translate({ id: 'CLIENT_RESEND_COOLDOWN' }) : ''}>
                <div
                    className={classes.sendEmailAgain}
                    data-cy="send-email-again">
                    <Button
                        className={classes.sendEmailAgainButton}
                        data-cy="send-email-again-button"
                        disabled={findDisabledEmail()}
                        onClick={() => sendEmailAgain()}
                    >
                        <MailOutlineIcon className={classes.sendEmailAgainIcon} />
                        {translate({ id: 'CLIENT_SEND_EMAIL_AGAIN_TEXT' })}
                    </Button>
                </div>
            </Tooltip>
        </div>
    );
};

export default ResendInvitationDialogBody;
